import React, { useState, useEffect } from 'react';
import { Card, CircularProgress, Box } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiCall } from '../main/ApiCall';
import '../css/main/Home.css';
import "../css/others/Table.css";
import { buildUserPayload } from '../main/buildUserPayload';
const FSDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true });
  const [error, setError] = useState<string | null>(null);
  const clientId = localStorage.getItem("clientId");
  const LoadingSpinner = ({ height = 200 }: { height?: number }) => (
    <Box display="flex" justifyContent="center" alignItems="center" height={height}>
      <CircularProgress />
    </Box>
  );
  const NoDataMessage = ({ height = 200 }: { height?: number }) => (
    <div className="no-data-available"> No data available </div>
  );
  useEffect(() => {
    const fetchData = async () => {
      let requestBody: { field: string; operator: string; value: string }[] = [{ field: "clientId", operator: "=", value: clientId ?? "" }];
      const payload = buildUserPayload();
      if (payload) {
        requestBody.push(payload);
      }
      try {
        setLoading({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true });
        setError(null);
        const response = await apiCall("post", `/api/dashboard/function/summary`, requestBody);
        setData(response);
      } catch (err: any) {
        console.error("Fetch error:", err);
        setError(err.message || "An unknown error occurred");
      } finally {
        setLoading({ auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false });
      }
    };
    fetchData();
  }, [clientId]);
  const findingsData = data?.functionFindings ? [
    { name: 'Not Implemented', value: data?.functionFindings?.notImplemented || 0, color: '#9E9E9E' },
    { name: 'Initial / Ad-hoc', value: data?.functionFindings?.initialAdhoc || 0, color: '#FFB74D' },
    { name: 'Partially Implemented', value: data?.functionFindings?.partiallyImplemented || 0, color: '#FFD54F' },
    { name: 'Implemented but Needs Improvement', value: data?.functionFindings?.improvements || 0, color: '#2196F3' },
    { name: 'Largely Effective', value: data?.functionFindings?.largelyEffective || 0, color: '#4DB6AC' },
    { name: 'Best Practice / Optimized', value: data?.functionFindings?.bestPractices || 0, color: '#4CAF50' },
  ] : [];
  const hasfindingsData = findingsData.some(item => item.value > 0);
  const findingsStatusData = data?.functionFindings?.ncCurrentStatus ? [
    { name: 'Closed', value: data?.functionFindings?.ncCurrentStatus.CLOSED || 0, color: '#4CAF50' },
    { name: 'Open', value: data?.functionFindings?.ncCurrentStatus.OPEN || 0, color: '#FFC107' },
    { name: 'Deferred', value: data?.functionFindings?.ncCurrentStatus.DEFERRED || 0, color: '#9C27B0' },
    { name: 'Work in Progress', value: data?.functionFindings?.ncCurrentStatus.WORK_IN_PROGRESS || 0, color: '#b13e08ff' },
  ].filter((item) => item.value > 0) : [];
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Function Spoc Dashboard</h1>
        <div className="dashboard-last-login">Last login: {new Date().toLocaleString()}</div>
      </div>
      <div className="dashboard-grid">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Function Audit Summary</h2>
          {loading.auditSummary ? (
            <LoadingSpinner height={318} />
          ) : error || !data ? (
            <NoDataMessage height={318} />
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.completedFunctionAudits || 0}</span>
                  <span className="dashboard-metric-label">Completed Audits</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.assignedFunctionAudits || 0}</span>
                  <span className="dashboard-metric-label">Assigned Audits</span>
                </div>
              </div>
              <div className="dashboard-chart-container">
                <div className="flex-1">
                  {hasfindingsData ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={findingsData} cx="50%" cy="50%" outerRadius={50} dataKey="value" labelLine={false}>
                          {findingsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <NoDataMessage height={200} />
                  )}
                </div>
                <div className="flex-1">
                  {findingsData.map((entry, index) => (
                    <div key={`findings-legend-${index}-${entry.name}`} className="findings-legend-item">
                      <div className="findings-legend-color" style={{ backgroundColor: entry.color }} />
                      <span className="findings-legend-text"> {entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Findings Status</h2>
          {loading.findingsStatus ? (
            <LoadingSpinner height={200} />
          ) : error || !data?.functionFindings?.ncCurrentStatus ? (
            <NoDataMessage height={200} />
          ) : findingsStatusData.length > 0 ? (
            <div className="dashboard-chart-container">
              <ResponsiveContainer width="90%" height={200}>
                <BarChart data={findingsStatusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value">
                    {findingsStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <NoDataMessage height={200} />
          )}
        </Card>
      </div>
      <div className="mt-20">
        <Card className="dashboard-card dashboard-full-width-card">
          <h2 className="dashboard-card-title">Functions Assigned</h2>
          {loading.projectsAssigned ? (
            <LoadingSpinner height={400} />
          ) : error || !data?.currentFunctions || data.currentFunctions.length === 0 ? (
            <NoDataMessage height={400} />
          ) : (
            <div className="dashboard-projects-container">
              {data.currentFunctions.map((func: any) => (
                <div key={func.functionId} className="dashboard-project-item">
                  <div className="dashboard-project-name">Function ID: {func.functionId}</div>
                  <div className="dashboard-project-name">Function Name: {func.functionName}</div>
                  <div className={`dashboard-project-status dashboard-project-status-${(func.status || '').toLowerCase().replace('_', '-')}`}>Function Status : {func.status.replace('_', ' ')}</div>
                  <div className="dashboard-project"> Report Submitted: {func.reportSubmitted === null ? 'Not Applicable' : func.reportSubmitted ? 'Yes' : 'No'}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
export default FSDashboard;